import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, TrendingUp, TrendingDown, CheckCircle, AlertCircle, Lightbulb, Crown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DailyReportCard } from "../types";

interface Props {
  report: DailyReportCard | null;
  isOpen: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
}

const GRADE_COLORS: Record<string, string> = {
  A: "from-emerald-400 to-green-500",
  B: "from-blue-400 to-indigo-500",
  C: "from-amber-400 to-yellow-500",
  D: "from-orange-400 to-red-400",
  F: "from-red-500 to-rose-600",
};

const ReportCardDetail: React.FC<Props> = ({ report, isOpen, onClose, onUpgrade }) => {
  const { t } = useTranslation();

  if (!report || !isOpen) return null;

  const gradientClass = GRADE_COLORS[report.grade] || GRADE_COLORS.C;

  const getStatusIcon = (status: string) => {
    if (status === "good" || status === "on_target") {
      return <CheckCircle size={16} className="text-success" />;
    }
    return <AlertCircle size={16} className="text-warning" />;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 backdrop-blur-sm z-50 flex items-end"
        style={{ backgroundColor: "var(--overlay)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="bg-surface rounded-t-3xl w-full max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-line-strong" />
          </div>

          {/* Header */}
          <div className="px-4 pb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradientClass} flex items-center justify-center text-2xl shadow-lg`}>
                {report.gradeEmoji}
              </div>
              <div>
                <h2 className="text-xl font-black text-content-primary">
                  {t("reportCard.grade")} {report.grade}
                </h2>
                <p className="text-xs text-content-tertiary font-medium">{report.date}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-inset flex items-center justify-center"
            >
              <X size={18} className="text-content-secondary" />
            </button>
          </div>

          <div className="px-4 pb-16 space-y-4">
            {/* Calorie Score */}
            <div className="card p-4">
              <h3 className="text-xs font-bold text-content-tertiary uppercase tracking-wider mb-3">
                {t("reportCard.calorieScore")}
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-black text-content-primary">
                    {report.calorieScore.consumed}
                  </p>
                  <p className="text-xs text-content-tertiary">
                    {t("reportCard.goal")}: {report.calorieScore.goal} kcal
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {report.calorieScore.status === "over" ? (
                    <TrendingUp className="text-danger" size={24} />
                  ) : report.calorieScore.status === "under" ? (
                    <TrendingDown className="text-warning" size={24} />
                  ) : (
                    <CheckCircle className="text-success" size={24} />
                  )}
                  <span className={`text-sm font-bold ${
                    report.calorieScore.status === "on_target" ? "text-success" :
                    report.calorieScore.status === "over" ? "text-danger" : "text-warning"
                  }`}>
                    {report.calorieScore.difference > 0 ? "+" : ""}{report.calorieScore.difference} kcal
                  </span>
                </div>
              </div>
            </div>

            {/* Macro Balance */}
            <div className="card p-4">
              <h3 className="text-xs font-bold text-content-tertiary uppercase tracking-wider mb-3">
                {t("reportCard.macros")}
              </h3>
              <div className="space-y-2.5">
                {(["protein", "carbs", "fats"] as const).map((macro) => (
                  <div key={macro} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(report.macroBalance[macro].status)}
                      <span className="text-sm font-medium text-content-primary">
                        {t(`home.${macro}`)}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-content-primary">
                      {report.macroBalance[macro].consumed}g
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Content */}
            {report.isPremium && report.highlights.length > 0 && (
              <>
                {/* Highlights */}
                <div className="card p-4">
                  <h3 className="text-xs font-bold text-success uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <CheckCircle size={14} />
                    {t("reportCard.highlights")}
                  </h3>
                  <div className="space-y-2">
                    {report.highlights.map((h, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-success mt-0.5 shrink-0">✨</span>
                        <span className="text-sm text-content-primary">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Improvements */}
                {report.improvements.length > 0 && (
                  <div className="card p-4">
                    <h3 className="text-xs font-bold text-info uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Lightbulb size={14} />
                      {t("reportCard.improvements")}
                    </h3>
                    <div className="space-y-2">
                      {report.improvements.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-info mt-0.5 shrink-0">💡</span>
                          <span className="text-sm text-content-primary">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tomorrow Tip */}
                {report.tomorrowTip && (
                  <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-4">
                    <h3 className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">
                      {t("reportCard.tomorrowTip")}
                    </h3>
                    <p className="text-sm text-slate-700">{report.tomorrowTip}</p>
                  </div>
                )}

                {/* Streak */}
                {report.streakAck && (
                  <div className="text-center py-1">
                    <p className="text-sm font-medium text-content-secondary">🔥 {report.streakAck}</p>
                  </div>
                )}
              </>
            )}

            {/* Free User Upsell */}
            {!report.isPremium && (
              <div className="rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-5 text-center">
                <Crown size={28} className="text-white mx-auto mb-2" />
                <h3 className="text-lg font-bold text-white mb-1">
                  {t("reportCard.upgradeTitle")}
                </h3>
                <p className="text-sm text-white/80 mb-4">
                  {t("reportCard.upgradeDesc")}
                </p>
                <button
                  onClick={onUpgrade}
                  className="w-full py-3 bg-white text-orange-600 font-bold rounded-xl shadow-lg active:scale-95 transition-transform"
                >
                  {t("premium.upgrade")}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReportCardDetail;
